import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ApiHandler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import { getOrderApproval } from "@insite/client-framework/Services/OrderApprovalService";
import { Session, watchForOtherTabSessionChange } from "@insite/client-framework/Services/SessionService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import {
    chain,
    NavigateToReturnUrl,
    SignInParameter,
    SignInResult,
} from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getSafeExternalUrl, getSafeRedirectUrl } from "@WhiteCap/Common/Utillities/SafeRedirect";
import { appendLegacyParam, checkSiteRestrictions } from "@WhiteCap/Common/Utillities/utils";
import { parseCommaSeparated } from "@WhiteCap/Common/Utillities/utils";

type HandlerType = ApiHandler<
    SignInParameter,
    SignInResult,
    {
        tokenSuccessfullyRequested?: boolean;
        authenticatedSession?: Session;
    }
>;

export const RedirectToChangeCustomerUpdated: HandlerType = async props => {
    if (!props.authenticatedSession?.redirectToChangeCustomerPageOnSignIn) {
        watchForOtherTabSessionChange(); // This sign-in failed, but another tab might be successful later on.
        return;
    }

    // bypass Change Customer page when user open invite link from Share List email
    const myListsDetailsPageUrl = getPageLinkByPageType(props.getState(), "MyListsDetailsPage")?.url;
    if (
        props.parameter.returnUrl &&
        myListsDetailsPageUrl &&
        props.parameter.returnUrl.toLowerCase().indexOf(myListsDetailsPageUrl.toLowerCase()) > -1 &&
        props.parameter.returnUrl.toLowerCase().indexOf("invite=") > -1
    ) {
        return;
    }
    // bypass Change Customer page when external user views orders
    const orderApprovalDetailsPageUrl = getPageLinkByPageType(props.getState(), "OrderApprovalDetailsPage")?.url;
    if (
        props.parameter.returnUrl &&
        orderApprovalDetailsPageUrl &&
        props.parameter.returnUrl.toLowerCase().indexOf(orderApprovalDetailsPageUrl.toLowerCase()) > -1 &&
        (props.parameter.returnUrl.toLowerCase().indexOf("external-approval=") > -1 ||
            props.parameter.returnUrl.toLowerCase().indexOf("external-approval-view=") > -1)
    ) {
        const parsedQuery = parseQueryString<{ cartId: string }>(props.parameter?.returnUrl?.split("?")[1] ?? "");
        const orderApproval = await getOrderApproval({
            cartId: parsedQuery.cartId,
        });

        const { billToId, shipToId, fulfillmentMethod, pickUpWarehouse, hasDefaultCustomer } = getSession(
            props.getState(),
        );

        if (
            orderApproval &&
            orderApproval.successful &&
            orderApproval.result?.billTo &&
            orderApproval.result?.shipTo &&
            (orderApproval.result.billTo.id !== billToId || orderApproval.result.shipTo.id !== shipToId)
        ) {
            props.dispatch(
                changeCustomerContext({
                    billToId: orderApproval.result.billTo.id,
                    shipToId: orderApproval.result.shipTo.id,
                    fulfillmentMethod,
                    pickUpWarehouse,
                    isDefault: hasDefaultCustomer,
                    skipRedirect: true,
                }),
            );
        }

        return;
    }

    const homePageUrl = getPageLinkByPageType(props.getState(), "HomePage")?.url;
    const dashboardPageUrl = getPageLinkByPageType(props.getState(), "MyAccountPage")?.url;
    const changeCustomerPageUrl = getPageLinkByPageType(props.getState(), "ChangeCustomerPage")?.url;
    if (homePageUrl && changeCustomerPageUrl) {
        const shouldAddReturnUrl = props.parameter.returnUrl && props.parameter.returnUrl !== homePageUrl;
        const returnUrl = shouldAddReturnUrl
            ? props.parameter.returnUrl
            : props.authenticatedSession.dashboardIsHomepage
              ? dashboardPageUrl!
              : undefined;

        window.location.href = changeCustomerPageUrl + (returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "");
        return false;
    }
};

/**
 * Waits for billTo data to be available in Redux store
 */
const waitForBillToData = async (getState: any, billToId: string, maxAttempts = 10, delayMs = 100): Promise<any> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const state = getState();
        const billToData = state.data?.billTos?.byId?.[billToId];

        if (billToData) {
            return billToData;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return null;
};

/**
 * Replaces NavigateToReturnUrl. After a successful sign-in, this handler
 * calls the session capture endpoint to obtain a one-time relay token,
 * then redirects directly to the Next.js relay route — producing a single
 * redirect instead of the double-redirect (OCC homepage → Next.js) that
 * occurred when the PostLoginRelayWidget was used.
 *
 * Special cases (external approval, VMI) that ran earlier in the chain
 * are unaffected because they stop the chain before this handler runs.
 */
export const CaptureAndRelayToNextJs: HandlerType = async props => {
    if (!props.authenticatedSession) {
        return;
    }

    // Guest checkout sessions should stay on OCC — do not relay to Next.js
    if (props.authenticatedSession.isGuest) {
        const returnUrl = props.parameter.returnUrl || getHomePageUrl(props.getState());
        if (returnUrl) {
            window.location.href = getSafeRedirectUrl(returnUrl);
            return false;
        }
        return;
    }

    const settings = props.getState().context.settings?.properties;
    const restrictedRoles = parseCommaSeparated(settings?.newSiteRestrictedRoles);
    const restrictedCustomers = parseCommaSeparated(settings?.newSiteRestrictedCustomers);

    const state = props.getState() as any;
    const session = getSession(state);
    const billToId = session?.billToId;

    let currentCustomerNumber: string | null = null;
    let currentBillTo = null;
    let isBillToLoading = false;

    if (billToId) {
        currentBillTo = state.data?.billTos?.byId?.[billToId];

        if (!currentBillTo) {
            currentBillTo = await waitForBillToData(() => props.getState(), billToId);
            isBillToLoading = !currentBillTo;
        }

        currentCustomerNumber = currentBillTo?.customerNumber;
    }

    // Get punchout session state for accurate checking
    const isPunchOutSession = getIsPunchOutSession(state);

    // Use centralized restriction logic
    const restrictionResult = checkSiteRestrictions({
        userName: props.authenticatedSession.userName,
        currentUserIsGuest: props.authenticatedSession.isGuest,
        isPunchOutSession,
        userRoles: props.authenticatedSession.userRoles,
        customerNumber: currentCustomerNumber,
        isBillToLoading: !!billToId && !currentBillTo,
        restrictedRoles,
        restrictedCustomers,
    });

    // Check if user should stay on legacy
    if (restrictionResult.shouldStayOnLegacy) {
        let returnUrl = props.parameter.returnUrl || getHomePageUrl(props.getState());
        if (returnUrl) {
            // Append legacy=true parameter
            returnUrl = appendLegacyParam(returnUrl);

            window.location.href = getSafeRedirectUrl(returnUrl);
        }
        return;
    }

    // If waiting for data, don't redirect yet
    if (restrictionResult.shouldWaitForData) {
        return;
    }

    const orderApprovalDetailsPageUrl = getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url;
    const isExternalApproval =
        !!props.parameter.returnUrl &&
        !!orderApprovalDetailsPageUrl &&
        props.parameter.returnUrl.toLowerCase().indexOf(orderApprovalDetailsPageUrl.toLowerCase()) > -1 &&
        (props.parameter.returnUrl.toLowerCase().indexOf("external-approval=") > -1 ||
            props.parameter.returnUrl.toLowerCase().indexOf("external-approval-view=") > -1);
    if (isExternalApproval) {
        window.location.href = getSafeRedirectUrl(props.parameter.returnUrl);
        return false;
    }

    // Capture the session cookie and obtain a one-time relay token
    try {
        // Mark that the handler is performing the relay so the
        // PostLoginRelayWidget (which covers SSO logins) does not double-fire.
        sessionStorage.setItem("crossdomain_relay_sent", "1");

        const resp = await fetch("/api/v1/whitecap/crossdomain/capture", {
            method: "POST",
            credentials: "same-origin",
        });

        if (resp.ok) {
            const data = await resp.json();
            const token = data?.relayToken;
            const relayBaseUrl = data?.relayUrl;

            if (token && relayBaseUrl) {
                const relayUrl = `${relayBaseUrl}?token=${encodeURIComponent(token)}`;

                const safeRelayUrl = getSafeExternalUrl(relayUrl);
                if (safeRelayUrl) {
                    window.location.href = safeRelayUrl;
                    return false;
                }
            }
        } else {
            console.error("Sign Capture API failed with status:", resp.status);
        }
    } catch (error) {
        console.error("Sign Capture API error:", error);
    }

    // Fallback: if capture failed, navigate to OCC homepage

    window.location.href = getSafeRedirectUrl(getHomePageUrl(state));
    return false;
};

// TODO: ECW-6309 Remove once finalized approach
// replaceInChain(chain, NavigateToReturnUrl, CaptureAndRelayToNextJs);
